import React, { useMemo } from 'react';
import StatsTable from '../../../common/tables/StatsTable.jsx';
import TooltipTableHeader from '../../../common/tooltips/TooltipTableHeader.jsx';
import { AdapterDayjs } from '@mui/x-date-pickers-v5/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-v5/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers-v5/DatePicker';
import { Box, Stack, IconButton, TextField, InputAdornment } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { MRT_ToggleGlobalFilterButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from 'material-react-table';
import ButtonTooltipTable from '../../../common/buttons/ButtonTooltipTable.jsx';
import { displayRequestRef } from '../../../../../utils.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { styled } from '@mui/material/styles';

const HtmlTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: '#F2F2F2',
		color: 'rgba(0, 0, 0, 0.87)',
		maxWidth: 220,
		border: '1px solid #DADDE9',
		fontSize: 12
	},
}));

export default function StatsSalesTable(props) {
	const {
		isLoaded,
		tableLoader = false,
		columnDef = {},
		columnVisibility = {},
		columnGrouping = [],
		data,
		currency = 'EUR',
		onColumnChange = () => false
	} = props;
	
	const total_ca_requests = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.ca_request ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const total_ht = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_total_providers_ht ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const total_ttc = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_total_ht ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_accomodation_ht = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_accommodations_ht ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_accomodation_ttc = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_accommodations_ttc ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_restauration_ht = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_restaurations_ht ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_restauration_ttc = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_restaurations_ttc ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_meeting_ht = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_meetings_ht ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_meeting_ttc = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_meetings_ttc ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_other_ht = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_others_ht ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_other_ttc = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_others_ttc ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_reductions_ht = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_reductions_ht ?? 0)
			), 0);
		}
		return 0;
	}, [data]);

	const subtotal_reductions_ttc = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (
				acc + parseFloat(curr.products_orders_subtotal_reductions_ttc ?? 0)
			), 0);			
		}
		return 0;
	}, [data]);

	const subtotal_tourist_tax = useMemo(() => {
			if (Array.isArray(data)) {
				return data.reduce((acc, curr) => (
					acc + parseFloat(curr.products_orders_subtotal_tourist_tax ?? 0)
				), 0);			
			}
			return 0;
	}, [data]);

	const total_qty = useMemo(() => {
		if (Array.isArray(data)) {
			return data.reduce((acc, curr) => (acc + 1), 0);			
		}
		return 0;
	}, [data]);

	const initialExpandedRootRows = useMemo(() => {
		let expended = {};
		const month = [...data.map((row) => 'month:' + (row.month ?? '')).filter((v, i, arr) => arr.indexOf(v) === i)];
		month.map((v) => expended[v] = true);
		return expended;
	}, [data]);

	const monthList = useMemo(() => {
		return [...data.map((row) => row.month).filter((v, i, arr) => arr.indexOf(v) === i)].sort().reverse();
	}, [data]);

	const yearList = useMemo(() => {
		return [...data.map((row) => row.year).filter((v, i, arr) => arr.indexOf(v) === i)].sort().reverse();
	}, [data]);

	const statusList = useMemo(() => {
		return [...data.map((row) => row.requests_hotels_status_key_trad).filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const declineCauseList = useMemo(() => {
		return [...data.map((row) => row.requests_hotels_decline_causes_key_trad ?? '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const requestEventList = useMemo(() => {
		return [...data.map((row) => row.requests_events_key_trad ?? '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const finalClientMembreGroupList = useMemo(() => {
		return [...data.map((row) => row.requests_final_client_membres_groups_name ?? row.requests_membres_groups_name).filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const finalClientCompanyTypeList = useMemo(() => {
		return [...data.map((row) => row.requests_final_client_membres_groups_organisations_type_key_trad ?? row.requests_membres_groups_organisations_type_key_trad).filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const businessIntroducerCompanyTypeList = useMemo(() => {
		return [...data.map((row) => row.business_introducer_membres_groups_organisations_type_key_trad ?? '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const sourceList = useMemo(() => {
		return [...data.map((row) => row.source).filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const hotelList = useMemo(() => {
		return [...data.map((row) => row.hotels_title).filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const managerRequestList = useMemo(() => {
		return [...data.map((row) => row.requests_manager_id ? (row.requests_manager_surname + ' ' + row.requests_manager_name) : '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const managerRequestHotelList = useMemo(() => {
		return [...data.map((row) => row.requests_hotels_manager_id ? (row.requests_hotels_manager_surname + ' ' + row.requests_hotels_manager_name) : '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const segmentList = useMemo(() => {
		return [...data.map((row) => row.segmentation_label ?? '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const requestTypeList = useMemo(() => {
		return [...data.map((row) => row.requests_type_key_trad ?? '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const commissionTypeList = useMemo(() => {
		return [...data.map((row) => row.hotels_societes_commission_type_key_trad ?? '').filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const currencyList = useMemo(() => {
		return [...data.map((row) => row.currency).filter((v, i, arr) => arr.indexOf(v) === i)].sort();
	}, [data]);

	const formsRequestsList = useMemo(() => {
		return [...data.map((row) => row.forms_requests_name).filter((v, i, arr) => v && arr.indexOf(v) === i)].sort();
	}, [data]);

	const renderGroupedCell = (cell) => (
		<Box sx={{ color: '#595B5C' }}>
			{cell.getValue()}
		</Box>
	)

	const columns = useMemo(() => [
		{
			id: 'requests_id',
			header: columnDef.requests_id.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_id,
			Cell: ({ cell }) => (
				cell.row.original.requests_hotels_id && (
					<Box sx={{ color: 'primary.main' }}>
						<a 
							href="#"
							data-part="+"
							data-target={`providers//requests_hotels/details/${cell.row.original.requests_hotels_id}`}
							className="js-nav get_part_link"
						>
							{ displayRequestRef(cell.getValue()) }
						</a>
					</Box>
				)
			)
		},
		{
			id: 'ref_hotel',
			header: columnDef.ref_hotel.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_hotels_ref_hotel ?? '',
			enableGrouping: false,
			Cell: ({ cell }) => (
				<HtmlTooltip title={<Box>{cell.getValue()}</Box>} disableInteractive>
					<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{cell.getValue()}
					</Box>
				</HtmlTooltip>
			)
		},
		{
			id: 'date_activated',
			header: columnDef.date_activated.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => new Date(row.requests_hotels_date_activated),
			size: 160,
			filterFn: 'lessThanOrEqualTo',
			sortingFn: 'datetime',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			Cell: ({ cell }) => cell.getValue()?.toLocaleDateString(ENV.lang.replace('_', '-'), { hour: '2-digit', minute:'2-digit' }),
			Filter: ({ column }) => (
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
					<DatePicker
						onChange={(newValue) => column.setFilterValue(newValue)}
						renderInput={({ inputRef, inputProps, InputProps }) => (
							<TextField 
								ref={inputRef}
								value={inputProps.value} 
								variant="standard"
								helperText={<><i className="fa-regular fa-filter"></i> 	&#60; {'or'.trad()} &#61;</>}
								InputProps={{
									endAdornment: <InputAdornment position='end'>{InputProps?.endAdornment}</InputAdornment>
								}}
							/>
						)}
					value={column.getFilterValue()}
				/>
				</LocalizationProvider>
			),
		},
		{
			id: 'source',
			header: columnDef.source.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.source,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: sourceList
		},
		{
			id: 'hotels_title',
			header: columnDef.hotels_title.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.hotels_title,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: hotelList
		},
		{
			id: 'property_ref',
			header: columnDef.property_ref.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.hotels_property_ref ?? '',
			enableGrouping: false,
			Cell: ({ cell }) => (
				<HtmlTooltip title={<Box>{cell.getValue()}</Box>} disableInteractive>
					<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{cell.getValue()}
					</Box>
				</HtmlTooltip>
			)
		},
		{
			id: 'rh_status',
			header: columnDef.rh_status.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_hotels_status_key_trad,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: statusList
		},
		{
			id: 'decline_cause',
			header: columnDef.decline_cause.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_hotels_decline_causes_key_trad ?? '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: declineCauseList
		},
		{
			id: 'decline_cause_details',
			header: columnDef.decline_cause_details.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_hotels_decline_causes_details ?? '',
			Cell: ({ cell }) => (
				<HtmlTooltip title={<Box>{cell.getValue()}</Box>} disableInteractive>
					<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{cell.getValue()}
					</Box>
				</HtmlTooltip>
			)
		},
		{
			id: 'nb_night',
			header: columnDef.nb_night.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.nb_night,
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			}
		},
		{
			id: 'nb_nuitees',
			header: columnDef.nb_nuitees.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.nb_nuitee,
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			}
		},
		{
			id: 'nb_nuitees_devis',
			header: columnDef.nb_nuitees_devis.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.nb_nuitee_devis,
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			}
		},
		{
			id: 'checkin',
			header: columnDef.checkin.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => new Date(row.requests_hotels_checkin ?? row.requests_checkin),
			size: 100,
			filterFn: 'lessThanOrEqualTo',
			sortingFn: 'datetime',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			Cell: ({ cell }) => cell.getValue()?.toLocaleDateString(ENV.lang.replace('_', '-')),
			Filter: ({ column }) => (
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
					<DatePicker
						onChange={(newValue) => column.setFilterValue(newValue)}
						renderInput={({ inputRef, inputProps, InputProps }) => (
							<TextField 
								ref={inputRef}
								value={inputProps.value} 
								variant="standard"
								helperText={<><i className="fa-regular fa-filter"></i> 	&#60; {'or'.trad()} &#61;</>}
								InputProps={{
									endAdornment: <InputAdornment position='end'>{InputProps?.endAdornment}</InputAdornment>
								}}
							/>
						)}
					value={column.getFilterValue()}
				/>
				</LocalizationProvider>
			),
		},
		{
			id: 'checkout',
			header: columnDef.checkout.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => new Date(row.requests_hotels_checkout ?? row.requests_checkout),
			size: 100,
			filterFn: 'lessThanOrEqualTo',
			sortingFn: 'datetime',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			Cell: ({ cell }) => cell.getValue()?.toLocaleDateString(ENV.lang.replace('_', '-')),
			Filter: ({ column }) => (
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
					<DatePicker
						onChange={(newValue) => column.setFilterValue(newValue)}
						renderInput={({ inputRef, inputProps, InputProps }) => (
							<TextField 
								ref={inputRef}
								value={inputProps.value} 
								variant="standard"
								helperText={<><i className="fa-regular fa-filter"></i> 	&#60; {'or'.trad()} &#61;</>}
								InputProps={{
									endAdornment: <InputAdornment position='end'>{InputProps?.endAdornment}</InputAdornment>
								}}
							/>
						)}
					value={column.getFilterValue()}
				/>
				</LocalizationProvider>
			),
		},
		{
			id: 'month',
			header: columnDef.month.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorKey: 'month',
			enableGrouping: true,
			size: 120,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: monthList
		},
		{
			id: 'year',
			header: columnDef.year.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorKey: 'year',
			enableGrouping: true,
			size: 100,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: yearList
		},
		{
			id: 'quantity',
			header: columnDef.quantity.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => 1,
			filterVariant: 'range',
			enableGrouping: false,
			size: 140,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => data.reduce((acc, curr) => acc + 1, 0),
			AggregatedCell: ({ cell, table }) => (
				<Box className='quantity' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{cell.getValue()}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'total_dossiers'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{total_qty}
					</Box>
				</Stack>
			),
		},
		{
			id: 'nb_pax',
			header: columnDef.nb_pax.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.nb_pax,
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			}
		},
		{
			id: 'requests_price',
			header: columnDef.requests_price.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_price > 0 ? row.requests_price : '',
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			Cell: ({ cell }) => (
				cell.getValue() > 0 && (
					<Box>
						{parseFloat(cell.getValue()).toLocaleString(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				)
			)
		},
		{
			id: 'ca_request',
			header: columnDef.ca_request.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.ca_request > 0 ? row.ca_request : '',
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.ca_request ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				cell.getValue() > 0 && (
				<Box>
					{parseFloat(cell.getValue()).toLocaleString(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
				)
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'total_general_ca_requests'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{total_ca_requests?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'total_ht',
			header: columnDef.total_ht.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_total_providers_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 190,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_total_providers_ht ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'total_general_devis_ht'.trad()} :
					<Box className='total-general-ht' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{total_ht?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'total_ttc',
			header: columnDef.total_ttc.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_total_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_total_ht ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'total_general_devis_ttc'.trad()} :
					<Box className='total-general-ttc' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{total_ttc?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_accomodation_ht',
			header: columnDef.subtotal_accomodation_ht.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_accommodations_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_accommodations_ht ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_accomodation_ht'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_accomodation_ht?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_accomodation_ttc',
			header: columnDef.subtotal_accomodation_ttc.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_accommodations_ttc ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_accommodations_ttc ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_accomodation_ttc'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_accomodation_ttc?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_restauration_ht',
			header: columnDef.subtotal_restauration_ht.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_restaurations_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_restaurations_ht ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_restauration_ht'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_restauration_ht?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_restauration_ttc',
			header: columnDef.subtotal_restauration_ttc.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_restaurations_ttc ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_restaurations_ttc ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_restauration_ttc'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_restauration_ttc?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_meeting_ht',
			header: columnDef.subtotal_meeting_ht.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_meetings_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_meetings_ht ?? 0)
					), 0);			
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_meeting_ht'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_meeting_ht?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_meeting_ttc',
			header: columnDef.subtotal_meeting_ttc.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_meetings_ttc ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_meetings_ttc ?? 0)
					), 0);					
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_meeting_ttc'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_meeting_ttc?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_other_ht',
			header: columnDef.subtotal_other_ht.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_others_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_others_ht ?? 0)
					), 0);				
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_other_ht'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_other_ht?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_other_ttc',
			header: columnDef.subtotal_other_ttc.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_others_ttc ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_others_ttc ?? 0)
					), 0);
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_other_ttc'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_other_ttc?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_reductions_ht',
			header: columnDef.subtotal_reductions_ht.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_reductions_ht ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_reductions_ht ?? 0)
					), 0);
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_reductions_ht'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_reductions_ht?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_reductions_ttc',
			header: columnDef.subtotal_reductions_ttc.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_reductions_ttc ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_reductions_ttc ?? 0)
					), 0);
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_reductions_ttc'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_reductions_ttc?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'subtotal_tourist_tax',
			header: columnDef.subtotal_tourist_tax.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.products_orders_subtotal_tourist_tax ?? 0,
			filterVariant: 'range',
			enableGrouping: false,
			size: 240,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			aggregationFn: (id, data) => {
				if (Array.isArray(data)) {
					return data.reduce((acc, curr) => (
						acc + parseFloat(curr.original.products_orders_subtotal_tourist_tax ?? 0)
					), 0);
				}
				return 0;
			},
			AggregatedCell: ({ cell, table }) => (
				<Box className='total' sx={{ color: 'primary.main', fontWeight: 'bold' }}>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Cell: ({ cell }) => (
				<Box className='total'>
					{parseFloat(cell.getValue())?.toLocaleString?.(ENV.lang.replace('_', '-'), {
						style: 'currency',
						currency: currency ?? 'EUR',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}
				</Box>
			),
			Footer: () => (
				<Stack sx={{ width: '100%' }} align='right'>
					{'subtotal_general_tourist_tax'.trad()} :
					<Box className='total' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
						{subtotal_tourist_tax?.toLocaleString?.(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				</Stack>
			),
		},
		{
			id: 'date_devis',
			header: columnDef.date_devis.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.date_devis ? new Date(row.date_devis) : '',
			size: 100,
			filterFn: 'lessThanOrEqualTo',
			sortingFn: 'datetime',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			Cell: ({ cell }) => cell.getValue() ? cell.getValue().toLocaleDateString(ENV.lang.replace('_', '-')) : '',
			Filter: ({ column }) => (
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
					<DatePicker
						onChange={(newValue) => column.setFilterValue(newValue)}
						renderInput={({ inputRef, inputProps, InputProps }) => (
							<TextField 
								ref={inputRef}
								value={inputProps.value} 
								variant="standard"
								helperText={<><i className="fa-regular fa-filter"></i> 	&#60; {'or'.trad()} &#61;</>}
								InputProps={{
									endAdornment: <InputAdornment position='end'>{InputProps?.endAdornment}</InputAdornment>
								}}
							/>
						)}
					value={column.getFilterValue()}
				/>
				</LocalizationProvider>
			),
		},
		{
			id: 'delais_reponse_devis',
			header: columnDef.delais_reponse_devis.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.delais_reponse_devis ?? '',
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			}
		},
		{
			id: 'type_groupe',
			header: columnDef.type_groupe.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_events_key_trad ?? '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: requestEventList
		},
		{
			id: 'client',
			header: columnDef.client.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_membres_groups_name ?? row.requests_membres_groups_name,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: finalClientMembreGroupList
		},
		{
			id: 'client_prenom',
			header: columnDef.client_prenom.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_surname ?? row.requests_membres_surname,
			enableGrouping: false,
		},
		{
			id: 'client_nom',
			header: columnDef.client_nom.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_name ?? row.requests_membres_name,
			enableGrouping: false,
		},
		{
			id: 'client_email',
			header: columnDef.client_email.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_email ?? row.requests_membres_email,
			enableGrouping: false,
		},
		{
			id: 'client_langue',
			header: columnDef.client_langue.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_langues_title ?? row.requests_membres_langues_title,
			enableGrouping: false,
		},
		{
			id: 'client_pays',
			header: columnDef.client_pays.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_pays_title ?? row.requests_membres_pays_title,
			enableGrouping: false,
		},
		{
			id: 'client_profile',
			header: columnDef.client_profile.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_final_client_membres_groups_organisations_type_key_trad ?? row.requests_membres_groups_organisations_type_key_trad,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: finalClientCompanyTypeList
		},
		{
			id: 'business_introducer_prenom',
			header: columnDef.business_introducer_prenom.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_surname ?? '',
			enableGrouping: false,
		},
		{
			id: 'business_introducer_nom',
			header: columnDef.business_introducer_nom.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_name ?? '',
			enableGrouping: false,
		},
		{
			id: 'business_introducer_email',
			header: columnDef.business_introducer_email.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_email ?? '',
			enableGrouping: false,
		},
		{
			id: 'business_introducer_langue',
			header: columnDef.business_introducer_langue.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_langues_title ?? '',
			enableGrouping: false,
		},
		{
			id: 'business_introducer_pays',
			header: columnDef.business_introducer_pays.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_pays_title ?? '',
			enableGrouping: false,
		},
		{
			id: 'business_introducer_groupe',
			header: columnDef.business_introducer_groupe.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_membres_groups_name ?? '',
			enableGrouping: false,
		},
		{
			id: 'business_introducer_groupe_type',
			header: columnDef.business_introducer_groupe_type.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.business_introducer_membres_groups_organisations_type_key_trad ?? '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: businessIntroducerCompanyTypeList
		},
		{
			id: 'business_introducer',
			header: columnDef.business_introducer.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_membres_groups_business_introducer ? 'yes'.trad() : 'no'.trad(),
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: ['yes'.trad(), 'no'.trad()]
		},
		{
			id: 'business_introducer_retrocession',
			header: columnDef.business_introducer_retrocession.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_membres_groups_business_introducer ? (row.requests_membres_groups_retrocession ?? '') : '',
			enableGrouping: false,
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			Cell: ({ cell }) => (
				cell.getValue() && (
					<Box>
						{(parseFloat(cell.getValue()) / 100).toLocaleString(ENV.lang.replace('_', '-'), {
							style: 'percent',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				)
			),
		},
		{
			id: 'business_introducer_estimated_commission',
			header: columnDef.business_introducer_estimated_commission.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_membres_groups_business_introducer ? (row.estimated_commission ?? parseFloat(0).toFixed(2)) : '',
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			Cell: ({ cell }) => (
				cell.getValue() && (
					<Box>
						{parseFloat(cell.getValue()).toLocaleString(ENV.lang.replace('_', '-'), {
							style: 'currency',
							currency: currency ?? 'EUR',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				)
			)
		},
		{
			id: 'destination',
			header: columnDef.destination.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.destination ?? '',
			enableGrouping: false
		},
		{
			id: 'segment',
			header: columnDef.segment.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.segmentation_label ?? '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: segmentList
		},
		{
			id: 'manager_requests',
			header: columnDef.manager_requests.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_manager_id ? (row.requests_manager_surname + ' ' + row.requests_manager_name) : '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: managerRequestList
		},
		{
			id: 'manager_requests_hotels',
			header: columnDef.manager_requests_hotels.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_hotels_manager_id ? (row.requests_hotels_manager_surname + ' ' + row.requests_hotels_manager_name) : '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: managerRequestHotelList
		},
		{
			id: 'ref_client',
			header: columnDef.ref_client.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_ref_client ?? '',
			enableGrouping: false,
			Cell: ({ cell }) => (
				<HtmlTooltip title={<Box>{cell.getValue()}</Box>} disableInteractive>
					<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{cell.getValue()}
					</Box>
				</HtmlTooltip>
			)
		},
		{
			id: 'facturation_name',
			header: columnDef.facturation_name.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.factures_destinataire ?? '',
			enableGrouping: false,
		},
		{
			id: 'facturation_address',
			header: columnDef.facturation_address.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.factures_destinataire_address ?? '',
			enableGrouping: false,
		},
		{
			id: 'facturation_postalcode',
			header: columnDef.facturation_postalcode.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.factures_destinataire_postalcode ?? '',
			enableGrouping: false,
		},
		{
			id: 'facturation_city',
			header: columnDef.facturation_city.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.factures_destinataire_city ?? '',
			enableGrouping: false,
		},
		{
			id: 'facturation_country',
			header: columnDef.facturation_country.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.factures_destinataire_country ?? '',
			enableGrouping: false,
		},
		{
			id: 'requests_type',
			header: columnDef.requests_type.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_type_key_trad ?? '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: requestTypeList
		},
		{
			id: 'reservation_date',
			header: columnDef.reservation_date.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.date_reservation ? new Date(row.date_reservation) : '',
			size: 100,
			filterFn: 'lessThanOrEqualTo',
			sortingFn: 'datetime',
			enableGrouping: false,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			Cell: ({ cell }) => cell.getValue() ? cell.getValue().toLocaleDateString(ENV.lang.replace('_', '-')) : '',
			Filter: ({ column }) => (
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
					<DatePicker
						onChange={(newValue) => column.setFilterValue(newValue)}
						renderInput={({ inputRef, inputProps, InputProps }) => (
							<TextField 
								ref={inputRef}
								value={inputProps.value} 
								variant="standard"
								helperText={<><i className="fa-regular fa-filter"></i> 	&#60; {'or'.trad()} &#61;</>}
								InputProps={{
									endAdornment: <InputAdornment position='end'>{InputProps?.endAdornment}</InputAdornment>
								}}
							/>
						)}
					value={column.getFilterValue()}
				/>
				</LocalizationProvider>
			),
		},
		{
			id: 'commission_rate',
			header: columnDef.commission_rate.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => parseFloat(row.hotels_societes_commission_rate) > 0 ? parseFloat(row.hotels_societes_commission_rate).toFixed(2) : '',
			filterVariant: 'range',
			enableGrouping: false,
			muiTableHeadCellProps: {
				align: 'right',
			},
			muiTableBodyCellProps: {
				align: 'right',
			},
			Cell: ({ cell }) => (
				cell.getValue() && (
					<Box>
						{(parseFloat(cell.getValue()) / 100).toLocaleString(ENV.lang.replace('_', '-'), {
							style: 'percent',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})}
					</Box>
				)
			)
		},
		{
			id: 'commission_type',
			header: columnDef.commission_type.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.hotels_societes_commission_type_key_trad ?? '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: commissionTypeList
		},
		{
			id: 'currency',
			header: columnDef.currency.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.currency,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: currencyList
		},
		{
			id: 'moderation',
			header: columnDef.moderation.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.hasOwnProperty('requests_products_orders_moderation') ? (row.requests_products_orders_moderation ? 'yes'.trad() : 'no'.trad()) : '',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: ['yes'.trad(), 'no'.trad()]
		},
		{
			id: 'date_decline',
			header: columnDef.date_decline.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.date_decline ? new Date(row.date_decline) : '',
			size: 160,
			filterFn: 'lessThanOrEqualTo',
			sortingFn: 'datetime',
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			Cell: ({ cell }) => cell.getValue() ? cell.getValue().toLocaleDateString(ENV.lang.replace('_', '-')) : '',
			Filter: ({ column }) => (
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ENV.lang.split('_').shift()}>
					<DatePicker
						onChange={(newValue) => column.setFilterValue(newValue)}
						renderInput={({ inputRef, inputProps, InputProps }) => (
							<TextField 
								ref={inputRef}
								value={inputProps.value} 
								variant="standard"
								helperText={<><i className="fa-regular fa-filter"></i> 	&#60; {'or'.trad()} &#61;</>}
								InputProps={{
									endAdornment: <InputAdornment position='end'>{InputProps?.endAdornment}</InputAdornment>
								}}
							/>
						)}
					value={column.getFilterValue()}
				/>
				</LocalizationProvider>
			),
		},
		{
			id: 'note',
			header: columnDef.note.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.requests_hotels_notes ?? '',
			Cell: ({ cell }) => (
				<HtmlTooltip title={<Box>{cell.getValue()}</Box>} disableInteractive>
					<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{cell.getValue()}
					</Box>
				</HtmlTooltip>
			)
		},
		{
			id: 'forms_requests_name',
			header: columnDef.forms_requests_name.label,
			Header: ({ column }) => <TooltipTableHeader title={column.columnDef.header} />,
			accessorFn: (row) => row.forms_requests_name,
			enableGrouping: true,
			GroupedCell: ({ cell }) => renderGroupedCell(cell),
			filterVariant: 'multi-select',
        	filterSelectOptions: formsRequestsList
		},
	], [data]);

	const handleChangeColumnVisibility = (columnVisibility) => {
		onColumnChange(columnVisibility);
	}

	// const handleExportRows = async (table) => {
	// 	const rows = table.getPreFilteredRowModel().rows
	// 	const cols = table.getFlatHeaders();
	// 	const formatExport = formatExportData(rows, null, cols);
	// 	return exportXLSX('stats_sales_filtered_data', formatExport);
	// };

	// const handleExportPDF = () => {
	// 	const doc = new jsPDF();
	// 	let totalPagesExp = '{total_pages_count_string}';
	// 	let pageSize = doc.internal.pageSize;
	// 	let pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
	// 	let pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

	// 	let elem = document.querySelector('#stats-sales-table');
	// 	let clone = elem.cloneNode(true);
	// 	clone.id = 'pdf-table';
	// 	clone.querySelectorAll('thead th span').forEach((el) => {
	// 		el.remove();
	// 	});
	// 	clone.querySelectorAll('.total').forEach((el) => {
	// 		el.innerText = el.innerText.replace(/\s+/, '');
	// 	});
	// 	let total_general_ht = clone.querySelector('.total-general-ht')?.innerText.replace(/\s+/, ' ');
	// 	let total_general_ttc = clone.querySelector('.total-general-ttc')?.innerText.replace(/\s+/, ' ');
	// 	elem.after(clone);

	// 	doc.autoTable({
	// 		startY: 20,
	// 		html: '#pdf-table',
	// 		margin: { left: 8, right: 8, top: 20 },
	// 		headStyles: {
	// 			fillColor: [0, 71, 83],
	// 			fontSize: 7
	// 		},
	// 		bodyStyles: {
	// 			fontSize: 7
	// 		},
	// 		footStyles: {
	// 			fontSize: 1,
	// 			fillColor: [255, 255, 255],
	// 			textColor: [255, 255, 255],
	// 		},
	// 		willDrawPage: function (data) {
	// 			// Header
	// 			doc.setFontSize(14);
	// 			doc.setTextColor(0, 71, 83);
	// 			doc.text('title'.trad(), pageWidth / 2, 15, { align: 'center' });
	// 		},
	// 		didDrawPage: function (data) {
	// 			// Footer
	// 			const nb = doc.internal.getNumberOfPages() + '/' + totalPagesExp;
	// 			doc.setFontSize(8);
	// 			doc.text(nb, pageWidth + 20, pageHeight - 10, { align: 'right' })
	// 		},
	// 	})

	// 	doc.setFontSize(11);
	// 	doc.setTextColor(0, 71, 83);
	// 	doc.text('total_general_devis_ht'.trad() + ' :  ' + total_general_ht, pageWidth - 10, doc.lastAutoTable.finalY + 20, { align: 'right' });
	// 	if (clone.querySelector('.total-general-ttc')) {
	// 		doc.text('total_general_devis_ttc'.trad() + ' :  ' + total_general_ttc, pageWidth - 10, doc.lastAutoTable.finalY + 30, { align: 'right' });
	// 	}
	// 	doc.putTotalPages(totalPagesExp);

	// 	const filename = formatXlsExportFilename(currentSociete.nom, 'stats_sales');
	// 	doc.save(`${filename}.pdf`);
	// 	clone.remove();
	// };

	return (
		<>
			<StatsTable
				id='stats-sales-table'
				columns={columns}
				data={data}
				isLoaded={isLoaded}
				tableLoader={tableLoader}
				striped={true}
				enableRowVirtualization
				enableDensityToggle={false}
				enableColumnResizing
				enableGrouping
				enableStickyHeader
				enableStickyFooter
				enablePagination
				//paginateExpandedRows={false}
				//enableColumnOrdering
				initialMRTState={{
					grouping: columnGrouping,
					pagination: { pageIndex: 0, pageSize: 100 },
					columnVisibility: columnVisibility,
					showColumnFilters: false,
					showGlobalFilter: false,
					expanded: initialExpandedRootRows
				}}
				tableContainerSxProps={{ 'maxHeight': '64vh' }}
				rowsPerPage={[50, 100, 200, 500, 1000]}
				onChangeColumnVisibility={handleChangeColumnVisibility}
				muiTopToolbarProps={{
					sx: {
						display: 'block',
						height: 'auto'
					}
				}}
				muiToolbarAlertBannerProps={{
					sx: {
						position: 'absolute',
						alignItems: 'center',
						maxWidth: '70%',
						backgroundColor: 'inherit'
					}
				}}
				muiToolbarAlertBannerChipProps={{
					size: 'small'
				}}
				renderToolbarInternalActions={({ table }) => (
					<>
						<MRT_ToggleGlobalFilterButton table={table} />
						<MRT_ToggleFiltersButton table={table} />
						<ButtonTooltipTable table={table} />
						<MRT_ToggleFullScreenButton table={table} />
					</>
				)}
				renderTopToolbarCustomActions={({ table }) => (
					<Box sx={{ display: 'flex', gap: '.4rem', p: '4px', flexWrap: 'wrap' }}>
						{/* <IconButton
							size="small"
							className="btn-table-icon"
							disabled={table.getPrePaginationRowModel().rows.length === 0}
							onClick={() => handleExportRows(table)}
						>
							<span>
								<span className="position-relative mr-1">
									<i className="fa-regular fa-file-excel"></i>
									<i className="fa-regular fa-filter position-absolute" style={{'fontSize': '10px', 'right': '-8px'}}></i>
								</span>
							</span>
						</IconButton> */}
						{/* <IconButton
							size="small"
							className="btn-table-icon"
							disabled={table.getRowModel().rows.length === 0}
							onClick={handleExportFullData}
						>
							<span>
								<span className="position-relative mr-1">
									<i className="fa-regular fa-file-excel"></i>
									<i className="fa-regular fa-database position-absolute" style={{ 'fontSize': '10px', 'right': '-10px' }}></i>
								</span>
							</span>
						</IconButton> */}
						{/* <IconButton
							size="small"
							className="btn-table-icon"
							disabled={table.getPrePaginationRowModel().rows.length === 0}
							onClick={handleExportPDF}
						>
							<span><i className="fa-regular fa-file-pdf"></i></span>
						</IconButton> */}
					</Box>
				)}
			/>
		</>
	);
}
